import { useEffect, useRef, useState } from 'react';
import {
  SESSION_SPLASH_AUTH_WAIT_MAX_MS,
  SESSION_SPLASH_EXIT_MS,
  SESSION_SPLASH_MIN_MS,
  shouldSkipSessionSplashForLocation,
} from '../auth/sessionSplash';
import { readPayplusPendingCheckout } from '../utils/payplusPayment';

export type SplashPhase = 'hidden' | 'active' | 'exiting';

type UseSessionSplashResult = {
  splashPhase: SplashPhase;
  progress: number;
};

function shouldSkipSessionSplash(): boolean {
  if (process.env.NODE_ENV === 'test') {
    return true;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  if (shouldSkipSessionSplashForLocation(window.location.pathname)) {
    return true;
  }
  // Browser Back from PayPlus often lands on /browse?asset= with pending checkout still stored.
  return Boolean(readPayplusPendingCheckout());
}

/**
 * Full-screen Placy landing splash on every cold app open, then cross-fade into home or login.
 * Skipped for PayPlus return URLs (and Back-from-PayPlus) so checkout handoff is not delayed by 2s.
 */
export function useSessionSplash(isAuthReady: boolean): UseSessionSplashResult {
  const skip = shouldSkipSessionSplash();
  const [splashPhase, setSplashPhase] = useState<SplashPhase>(() => (skip ? 'hidden' : 'active'));
  const [progress, setProgress] = useState(() => (skip ? 100 : 0));
  const exitTimerRef = useRef<number | undefined>(undefined);
  const startedAtRef = useRef<number | null>(null);
  const isAuthReadyRef = useRef(isAuthReady);
  isAuthReadyRef.current = isAuthReady;

  useEffect(() => {
    return () => {
      if (exitTimerRef.current !== undefined) {
        window.clearTimeout(exitTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (splashPhase !== 'active') {
      return;
    }

    if (startedAtRef.current === null) {
      startedAtRef.current = performance.now();
    }
    let finished = false;

    const tick = () => {
      if (finished) {
        return;
      }

      const startedAt = startedAtRef.current ?? performance.now();
      const elapsed = performance.now() - startedAt;
      const authBootstrapDone =
        isAuthReadyRef.current || elapsed >= SESSION_SPLASH_AUTH_WAIT_MAX_MS;
      const canFinish = authBootstrapDone && elapsed >= SESSION_SPLASH_MIN_MS;
      const timeRatio = Math.min(1, elapsed / SESSION_SPLASH_MIN_MS);
      const next = canFinish ? 100 : Math.min(98, Math.floor(timeRatio * 98));
      setProgress(next);

      if (canFinish) {
        finished = true;
        setSplashPhase('exiting');
        exitTimerRef.current = window.setTimeout(() => {
          setSplashPhase('hidden');
        }, SESSION_SPLASH_EXIT_MS);
      }
    };

    tick();
    const id = window.setInterval(tick, 40);
    return () => window.clearInterval(id);
  }, [splashPhase]);

  return { splashPhase, progress };
}
